// @oasis-permission-inherits: /capcode (capcode:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  formatCost,
  formatDateTime,
  statusVariant,
  verdictVariant,
} from "~/modules/capcode-review/format";
import {
  getCapCodeReviewArtifactQuery,
  getCapCodeReviewPRQuery,
} from "~/modules/capcode-review/queries";
import type { ReviewRunSummary } from "~/modules/capcode-review/schemas";

export const Route = createFileRoute("/_dash/capcode/reviews/$docId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { docId } = Route.useParams();
  const {
    data: pr,
    isLoading,
    error,
  } = getCapCodeReviewPRQuery.useQuery({
    params: { docId },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !pr) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/capcode/reviews" className="text-primary text-sm hover:underline">
          ← Back to reviews
        </Link>
        <div className="text-red-600 text-sm">
          Failed to load PR: {error ? String(error) : "not found"}
        </div>
      </div>
    );
  }

  const prUrl = pr.repo ? `https://github.com/${pr.repo}/pull/${pr.pr}` : undefined;

  return (
    <div className="flex flex-col gap-4">
      <Link to="/capcode/reviews" className="text-primary text-sm hover:underline">
        ← Back to reviews
      </Link>

      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">
          {pr.repo}#{pr.pr}
        </h1>
        {pr.closed ? <Badge variant="secondary">closed</Badge> : null}
        {prUrl ? (
          <a
            href={prUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary text-sm hover:underline"
          >
            Open on GitHub ↗
          </a>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cumulative cost</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <Stat label="Total cost" value={formatCost(pr.cost.totalCostUsd)} />
          <Stat label="Runs" value={String(pr.cost.runCount)} />
          <Stat label="Input tokens" value={pr.cost.totalInputTokens.toLocaleString()} />
          <Stat label="Output tokens" value={pr.cost.totalOutputTokens.toLocaleString()} />
          <Stat label="Total turns" value={String(pr.cost.totalTurns)} />
          <Stat label="Last model" value={pr.cost.lastModel ?? "—"} />
          <Stat label="Reviewed SHA" value={pr.sha ? pr.sha.slice(0, 10) : "—"} mono />
          <Stat
            label="No-verdict SHA"
            value={pr.noVerdictSha ? pr.noVerdictSha.slice(0, 10) : "—"}
            mono
          />
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-2 text-lg font-semibold">Runs ({pr.runs.length})</h2>
        {pr.runs.length === 0 ? (
          <p className="text-muted-foreground text-sm">No runs recorded.</p>
        ) : (
          <Accordion
            type="multiple"
            defaultValue={pr.runs.map((run) => run.runId)}
            className="rounded-md border"
          >
            {pr.runs.map((run) => (
              <RunRow key={run.runId} docId={docId} run={run} />
            ))}
          </Accordion>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={mono ? "font-mono text-sm" : "text-sm"}>{value}</span>
    </div>
  );
}

function RunRow({ docId, run }: { docId: string; run: ReviewRunSummary }) {
  return (
    <AccordionItem value={run.runId} className="px-4">
      <AccordionTrigger className="hover:no-underline">
        <div className="flex flex-1 flex-wrap items-center gap-2 pr-4 text-left">
          <Badge variant={statusVariant(run.status)}>{run.status || "unknown"}</Badge>
          {run.outcome.verdict ? (
            <Badge variant={verdictVariant(run.outcome.verdict)}>{run.outcome.verdict}</Badge>
          ) : null}
          {run.outcome.action ? <Badge variant="outline">{run.outcome.action}</Badge> : null}
          {run.strict ? <Badge variant="secondary">strict</Badge> : null}
          <span className="text-sm font-medium">{run.kind || "run"}</span>
          <span className="text-muted-foreground text-xs">trigger: {run.trigger || "—"}</span>
          <span className="text-muted-foreground ml-auto text-xs">
            {formatDateTime(run.createdAt)}
          </span>
          <span className="text-muted-foreground text-xs">{formatCost(run.cost.totalCostUsd)}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex flex-col gap-4 pb-2">
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Stat label="Run ID" value={run.runId} mono />
            <Stat label="Head SHA" value={run.headSha ? run.headSha.slice(0, 10) : "—"} mono />
            <Stat label="Model" value={run.model ?? "—"} />
            <Stat label="Text model" value={run.textModel ?? "—"} />
            <Stat label="Confidence" value={run.outcome.confidence ?? "—"} />
            <Stat label="Inline findings" value={String(run.outcome.inlineCount)} />
            <Stat label="Dropped (out of diff)" value={String(run.outcome.droppedOutOfDiff)} />
            <Stat label="Posted" value={run.outcome.posted ? "yes" : "no"} />
            <Stat label="Strict mode" value={run.strict ? "yes" : "no"} />
            <Stat label="Cost" value={formatCost(run.cost.totalCostUsd)} />
            <Stat label="Input tokens" value={run.cost.inputTokens.toLocaleString()} />
            <Stat label="Output tokens" value={run.cost.outputTokens.toLocaleString()} />
            <Stat label="Turns" value={String(run.cost.numTurns)} />
          </div>

          {run.outcome.skipReason ? (
            <Field label="Skip reason">{run.outcome.skipReason}</Field>
          ) : null}
          {run.outcome.reason ? <Field label="Reason">{run.outcome.reason}</Field> : null}
          {run.outcome.summary ? <Field label="Summary">{run.outcome.summary}</Field> : null}
          {run.outcome.error ? (
            <Field label="Error">
              <span className="text-red-600">{run.outcome.error}</span>
            </Field>
          ) : null}

          {run.filesChanged.length > 0 ? (
            <Field label={`Files changed (${run.filesChanged.length})`}>
              <ul className="font-mono text-xs">
                {run.filesChanged.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </Field>
          ) : null}

          {run.artifacts.length > 0 ? (
            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-xs">Artifacts</span>
              <div className="flex flex-col gap-2">
                {run.artifacts.map((kind) => (
                  <ArtifactViewer key={kind} docId={docId} runId={run.runId} kind={kind} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="text-sm whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function ArtifactViewer({ docId, runId, kind }: { docId: string; runId: string; kind: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = getCapCodeReviewArtifactQuery.useQuery(
    { params: { docId, runId, kind } },
    { enabled: open }
  );

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hover:bg-muted/50 flex w-full items-center justify-between px-3 py-2 text-left text-sm"
      >
        <span className="font-mono">{kind}</span>
        <span className="text-muted-foreground text-xs">{open ? "hide" : "show"}</span>
      </button>
      {open ? (
        <div className="border-t px-3 py-2">
          {isLoading && <Skeleton className="h-24 w-full" />}
          {!isLoading && error ? (
            <span className="text-red-600 text-xs">Failed to load: {String(error)}</span>
          ) : null}
          {!isLoading && !error ? (
            <>
              <div className="text-muted-foreground mb-2 flex items-center gap-3 text-xs">
                <span>{data?.totalChars.toLocaleString()} chars</span>
                <span>{data?.totalChunks} chunk(s)</span>
                {data ? <CopyButton text={data.content} /> : null}
              </div>
              <pre className="bg-muted max-h-[480px] overflow-auto rounded p-3 text-xs whitespace-pre-wrap">
                {data?.content || "(empty)"}
              </pre>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 px-2 text-xs"
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? "copied" : "copy"}
    </Button>
  );
}
